import React, { useEffect, useRef, useState } from 'react'
import {
  FieldTimeOutlined,
  GlobalOutlined,
  InsertRowLeftOutlined,
  LineChartOutlined,
  PlayCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Checkbox, Dropdown, Radio, Button, Input, Row, Col } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import {
  BRANDS_INSIGHTS_ACTIVE_TAB,
  ADVANCED_FILTERS,
  ADVANCED_FILTER_CLICKED,
  CROSS_ICON_FILTER,
  LANDING_PAGES_DATA,
  CLEAR_ALL_FILTERS,
  AD_ACTIVITY_DATA,
  TRIGGER_SEARCH,
  AD_COPY_DATA,
  HEADLINES_DATA,
  HOOKS_DATA,
  MEDIA_MIX_DATA,
} from '@utils/constants'
import { clearAllFiltersData } from '@services/applyAdvancedFilters'
import styles from './filters.module.css'
import industryOptions from './industryOptions.json'
import { googleCountries, metaCountries } from '../../data/discoverData'
import FiltersButton from './FiltersButton'
import SelectedFilters from './SelectedFilters'

const Filters = ({ pageName }) => {
  const clearAllFilterStates = useSelector(state => state?.discoverAds?.clearAllFilterStates)
  const clearAllFilters = useSelector(state => state?.discoverAds?.clearAllFilters)
  const selectedDescriptions = useSelector(state => state?.discoverAds?.selectedDescriptions || [])
  const selectedHeadlines = useSelector(state => state?.discoverAds?.selectedHeadlines || [])
  const selectedLandingPages = useSelector(state => state?.discoverAds?.selectedLandingPages || [])
  const selectedHooks = useSelector(state => state?.discoverAds?.selectedHooks || [])
  const selectedChannel = useSelector(state => state?.discoverAds?.channels)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('adIndustry')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFilters, setSelectedFilters] = useState({
    industry: [],
    platform: [],
    performance: [],
    adIndustry: [],
    location: [],
    language: [],
    hooks: [],
    descriptions: [],
    headlines: [],
    landingPages: [],
  })
  const containerRef = useRef(null)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch({ type: TRIGGER_SEARCH, payload: true })
    const newFilters = { ...selectedFilters }
    // Add all selected items to filters
    newFilters.descriptions = selectedDescriptions
    newFilters.headlines = selectedHeadlines
    newFilters.landingPages = selectedLandingPages
    newFilters.hooks = selectedHooks
    dispatch({
      type: ADVANCED_FILTERS,
      payload: newFilters,
    })
    setSelectedFilters(newFilters)
    setTimeout(() => {
      dispatch({ type: TRIGGER_SEARCH, payload: false })
    }, 300)
    // Dispatch the updated filters
    // eslint-disable-next-line
  }, [selectedDescriptions, selectedHeadlines, selectedLandingPages, selectedHooks])

  // Function to get display name for category
  const getFilterData = () => {
    const data = {
      adIndustry: {
        name: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <InsertRowLeftOutlined style={{ color: '#f16434', fontSize: 14 }} />
            <div style={{ fontSize: 14 }}>Industry</div>
          </div>
        ),
        value: industryOptions?.adIndustry,
      },
      ...(selectedChannel === 'Meta' || selectedChannel === 'All' || pageName === 'BrandsDetail'
        ? {
            language: {
              name: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <GlobalOutlined style={{ color: '#f16434', fontSize: 14 }} />
                  <div style={{ fontSize: 14 }}>Language</div>
                </div>
              ),
              value: metaCountries,
            },
          }
        : {}),
      ...(selectedChannel === 'Google' || selectedChannel === 'All' || pageName === 'BrandsDetail'
        ? {
            location: {
              name: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <GlobalOutlined style={{ color: '#f16434', fontSize: 14 }} />
                  <div style={{ fontSize: 14 }}>Location</div>
                </div>
              ),
              value: googleCountries,
            },
          }
        : {}),
      industry: {
        name: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FieldTimeOutlined style={{ color: '#f16434', fontSize: 14 }} />
            <div style={{ fontSize: 14 }}>Run Time</div>
          </div>
        ),
        value: [
          { label: 'Under 7 Days', value: 'Under 7 Days' },
          { label: 'Over 7 Days', value: 'Over 7 Days' },
          { label: 'Over 15 Days', value: 'Over 15 Days' },
          { label: 'Over 30 Days', value: 'Over 30 Days' },
          { label: 'Over 3 Months', value: 'Over 3 Months' },
          { label: 'Over 6 Months', value: 'Over 6 Months' },
          { label: 'Over 12 Months', value: 'Over 12 Months' },
        ],
      },
    }
    data.performance = {
      name: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LineChartOutlined style={{ color: '#f16434', fontSize: 14 }} />
          <div style={{ fontSize: 14 }}>Performance</div>
        </div>
      ),
      value: [
        { label: 'Excellent', value: 'Excellent' },
        { label: 'Good', value: 'Good' },
        { label: 'Average', value: 'Average' },
        { label: 'Below Average', value: 'Below Average' },
        { label: 'Poor', value: 'Poor' },
      ],
    }

    data.platform = {
      name: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PlayCircleOutlined style={{ color: '#f16434', fontSize: 14 }} />
          <div style={{ fontSize: 14 }}>Active Status</div>
        </div>
      ),
      value: [
        { label: 'Active', value: 'active' },
        { label: 'Ended', value: 'inactive' },
      ],
    }
    return data
  }

  const filterData = getFilterData()
  const getSelectedCount = category => selectedFilters[category]?.length

  // Usage

  const handleFilterChange = (option, category) => {
    setSelectedFilters(prev => {
      let updatedValues
      if (category === 'industry' || category === 'location' || category === 'language') {
        updatedValues = [option]
      } else {
        // For other categories (checkbox), keep the existing toggle behavior
        const prevValues = prev[category] || []
        updatedValues = prevValues.includes(option)
          ? prevValues.filter(item => item !== option)
          : [...prevValues, option]
      }

      const updatedFilters = {
        ...prev,
        [category]: updatedValues,
      }

      const apiFilters = {
        industry: updatedFilters.industry,
        platform: updatedFilters.platform,
        performance: updatedFilters.performance,
        adIndustry: updatedFilters.adIndustry,
        language: updatedFilters.language,
        location: updatedFilters.location,
        descriptions: selectedDescriptions,
        headlines: selectedHeadlines,
        landingPages: selectedLandingPages,
        hooks: selectedHooks,
      }
      dispatch({ type: ADVANCED_FILTERS, payload: apiFilters })
      dispatch({ type: TRIGGER_SEARCH, payload: true })
      setTimeout(() => {
        dispatch({ type: TRIGGER_SEARCH, payload: false })
      }, 300)
      return updatedFilters
    })
  }

  const clearAllCategories = async () => {
    setSelectedFilters({
      adIndustry: [],
      industry: [],
      platform: [],
      performance: [],
      language: [],
      location: [],
      descriptions: [],
      headlines: [],
      hooks: [],
      landingPages: [],
    })
    const apiFilters = {
      adIndustry: [],
      industry: [],
      platform: [],
      performance: [],
      language: [],
      location: [],
      descriptions: [],
      headlines: [],
      hooks: [],
      landingPages: [],
    }
    dispatch({ type: ADVANCED_FILTERS, payload: apiFilters })
    dispatch({ type: 'SET_SELECTED_DESCRIPTIONS', payload: [] })
    dispatch({ type: 'SET_SELECTED_HEADLINES', payload: [] })
    dispatch({ type: 'SET_SELECTED_LANDING_PAGES', payload: [] })
    dispatch({ type: 'SET_SELECTED_HOOKS', payload: [] })
    await clearAllFiltersData(dispatch)
  }
  const crossButtonClick = async () => {
    setSelectedFilters({
      industry: [],
      platform: [],
      performance: [],
      adIndustry: [],
      language: [],
      location: [],
    })
    const apiFilters = {
      industry: [],
      platform: [],
      performance: [],
      adIndustry: [],
      language: [],
      location: [],
    }
    dispatch({ type: ADVANCED_FILTERS, payload: apiFilters })
    dispatch({ type: 'SET_SELECTED_DESCRIPTIONS', payload: [] })
    dispatch({ type: 'SET_SELECTED_HEADLINES', payload: [] })
    dispatch({ type: 'SET_SELECTED_LANDING_PAGES', payload: [] })
    dispatch({ type: 'SET_SELECTED_HOOKS', payload: [] })
    dispatch({ type: CLEAR_ALL_FILTERS, payload: true })
    dispatch({ type: BRANDS_INSIGHTS_ACTIVE_TAB, payload: 'overview' })
    dispatch({ type: AD_ACTIVITY_DATA, payload: [] })
    dispatch({ type: AD_COPY_DATA, payload: [] })
    dispatch({ type: HEADLINES_DATA, payload: [] })
    dispatch({ type: LANDING_PAGES_DATA, payload: [] })
    dispatch({ type: MEDIA_MIX_DATA, payload: [] })
    dispatch({ type: HOOKS_DATA, payload: [] })
  }

  useEffect(() => {
    clearAllCategories()
    // eslint-disable-next-line
  }, [selectedChannel])
  useEffect(() => {
    crossButtonClick()
    // eslint-disable-next-line
  }, [clearAllFilters])
  useEffect(() => {
    if (clearAllFilterStates === true) {
      crossButtonClick()
    }
    // eslint-disable-next-line
  }, [clearAllFilterStates])

  const filterPopupContent = (
    // eslint-disable-next-line
    <div className={styles.filterPopupCard} onClick={e => e.stopPropagation()}>
      <div className={styles.filterSection}>
        <div className={styles.filterCategoriesContainer}>
          {Object.keys(filterData).map(category => (
            // eslint-disable-next-line
            <div
              key={category}
              className={
                selectedCategory === category
                  ? styles.filterCategorySelected
                  : styles.filterCategory
              }
              onClick={() => {
                setSelectedCategory(category)
                setSearchTerm('')
              }}
            >
              <div className={styles.categoryName}>
                <span>{filterData[category].name}</span>
                {getSelectedCount(category) > 0 && (
                  <span className={styles.categoryCount}>{getSelectedCount(category)}</span>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className={styles.filterOptions}>
          {(selectedCategory === 'adIndustry' ||
            selectedCategory === 'language' ||
            selectedCategory === 'location') && (
            <div className={styles.searchContainer}>
              <Input
                prefix={<SearchOutlined />}
                placeholder={
                  // eslint-disable-next-line
                  selectedCategory === 'adIndustry'
                    ? 'Search industries...'
                    : selectedCategory === 'language'
                    ? 'Search languages...'
                    : 'Search locations...'
                }
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          )}
          <div className={styles.optionsContent}>
            {selectedCategory === 'industry' ||
            selectedCategory === 'location' ||
            selectedCategory === 'language' ? (
              <Radio.Group
                value={selectedFilters[selectedCategory]?.[0]}
                onChange={e => handleFilterChange(e.target.value, selectedCategory)}
              >
                {filterData[selectedCategory]?.value
                  .filter(item =>
                    selectedCategory === 'adIndustry' ||
                    selectedCategory === 'language' ||
                    selectedCategory === 'location'
                      ? item.label.toLowerCase().includes(searchTerm.toLowerCase())
                      : true
                  )
                  .map(({ label, value }) => (
                    <Radio key={value} value={value} className={styles.styledRadio}>
                      <div className={styles.optionContainer}>
                        <span>{label}</span>
                      </div>
                    </Radio>
                  ))}
              </Radio.Group>
            ) : (
              filterData[selectedCategory]?.value
                .filter(item =>
                  selectedCategory === 'adIndustry' ||
                  selectedCategory === 'language' ||
                  selectedCategory === 'location'
                    ? item.label.toLowerCase().includes(searchTerm.toLowerCase())
                    : true
                )
                .map(({ label, value }) => (
                  <Checkbox
                    key={value}
                    checked={selectedFilters[selectedCategory]?.includes(value)}
                    onChange={() => handleFilterChange(value, selectedCategory)}
                    className={styles.styledCheckbox}
                  >
                    <div className={styles.optionContainer}>
                      <span>{label}</span>
                    </div>
                  </Checkbox>
                ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
  return (
    <div ref={containerRef}>
      <Row gutter={20}>
        {pageName !== 'BrandsDetail' ? <Col span={1} /> : ''}
        <Col span={pageName === 'BrandsDetail' ? 17 : 16} style={{ paddingLeft: 25 }}>
          <SelectedFilters
            selectedDescriptions={selectedDescriptions}
            selectedHeadlines={selectedHeadlines}
            selectedLandingPages={selectedLandingPages}
            selectedHooks={selectedHooks}
            selectedFilters={selectedFilters}
            getFilterData={getFilterData}
            dispatch={dispatch}
            setSelectedFilters={setSelectedFilters}
            ADVANCED_FILTERS={ADVANCED_FILTERS}
            CROSS_ICON_FILTER={CROSS_ICON_FILTER}
          />
        </Col>
        <Col span={7}>
          {pageName === 'BrandsDetail' ? (
            <Row>
              <Col span={12} />
              <Col span={12} style={{ marginLeft: -26 }}>
                <FiltersButton
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                  selectedFilters={selectedFilters}
                  clearAllCategories={clearAllCategories}
                  crossButtonClick={crossButtonClick}
                  containerRef={containerRef}
                  filterPopupContent={filterPopupContent}
                />
              </Col>
            </Row>
          ) : (
            <FiltersButton
              crossButtonClick={crossButtonClick}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              selectedFilters={selectedFilters}
              clearAllCategories={clearAllCategories}
              containerRef={containerRef}
              filterPopupContent={filterPopupContent}
            />
          )}
        </Col>
      </Row>
    </div>
  )
}

export default Filters
